import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import * as configImportClient from './api/config-import-client';
import type { BrowseResult, ConfigListEntry, ImportFinding, ImportResult } from './api/config-import-client';
import { apiErrorCode } from '@/lib/api-client';
import { navigateToLinkTarget } from '../lib/link-target-path';
import styles from './tenant-settings.module.css';

/** Maps a known 409 error code to a friendly inline message — same
 *  convention as AddFormDialog.tsx's messageForError. */
function messageForImportError(err: unknown): string {
  if (apiErrorCode(err) === 'duplicate_name') {
    return 'A job type with that name already exists — pick another name.';
  }
  return 'Could not import this config. Please try again.';
}

interface GroupedEntry {
  jobTypeSlug: string;
  latest: ConfigListEntry;
  history: ConfigListEntry[];
}

/** Groups the flat browse list by jobTypeSlug and splits each group into
 *  the highest-versionNumber row (shown by default) and the rest (revealed
 *  by the per-row "history" toggle). */
function groupBySlug(items: ConfigListEntry[]): GroupedEntry[] {
  const bySlug = new Map<string, ConfigListEntry[]>();
  for (const item of items) {
    const list = bySlug.get(item.jobTypeSlug) ?? [];
    list.push(item);
    bySlug.set(item.jobTypeSlug, list);
  }
  return Array.from(bySlug.entries()).map(([jobTypeSlug, entries]) => {
    const sorted = [...entries].sort((a, b) => b.versionNumber - a.versionNumber);
    return { jobTypeSlug, latest: sorted[0]!, history: sorted.slice(1) };
  });
}

function formatPushedAt(pushedAt: string | null): string {
  // History rows come back with pushedAt: null by design (T6 — no lazy
  // per-version fetch in v1); render them without a date rather than
  // guessing or refetching.
  if (!pushedAt) return '—';
  return new Date(pushedAt).toLocaleDateString();
}

type Selection = { source: 'github'; entry: ConfigListEntry } | { source: 'upload'; file: File };

interface ImportReportViewProps {
  report: ImportResult;
  targetName: string;
  rechecking: boolean;
  onRecheck: () => void;
  onDone: () => void;
  onFindingClick: (target: NonNullable<ImportFinding['linkTarget']>) => void;
  /** Opens the new job type via client-side nav (not a raw anchor jump —
   *  the href stays for accessibility/right-click, but a plain left-click
   *  goes through `useNavigate` like the finding buttons below do, so
   *  neither internal link in this report forces a full page reload). */
  onOpenJobType: () => void;
}

/** The resolution report — one render path shared by both transports (design
 *  §4): whichever client call `handleImport` made, its `ImportResult` lands
 *  here identically. Finding text is rendered verbatim (name-ful vs id-only
 *  wording lives entirely in the message string, see Task 3); a finding with
 *  `linkTarget` is a button, otherwise plain text. */
function ImportReportView({ report, targetName, rechecking, onRecheck, onDone, onFindingClick, onOpenJobType }: ImportReportViewProps) {
  const hasErrors = report.errors.length > 0;
  return (
    <div className={styles.importReport}>
      <p className={hasErrors ? styles.reportSummary : styles.testOk}>
        {hasErrors
          ? `Imported — ${report.errors.length} issue${report.errors.length === 1 ? '' : 's'} to resolve before publish.`
          : 'Imported — 0 issues, ready to publish.'}{' '}
        <a
          href={`/admin/job-types/${report.jobTypeId}`}
          className={styles.reportLink}
          onClick={(e) => {
            e.preventDefault();
            onOpenJobType();
          }}
        >
          {targetName}
        </a>
      </p>
      {report.healedCount > 0 && (
        <p className={styles.reportSummary}>
          Healed {report.healedCount} reference{report.healedCount === 1 ? '' : 's'} by name.
        </p>
      )}
      {hasErrors && (
        <ul className={styles.reportErrorList}>
          {report.errors.map((finding, i) => (
            <li key={i}>
              {finding.linkTarget ? (
                <button
                  type="button"
                  className={`${styles.reportErrorRow} ${styles.reportErrorLink}`}
                  onClick={() => onFindingClick(finding.linkTarget!)}
                >
                  {finding.message}
                </button>
              ) : (
                <span className={styles.reportErrorRow}>{finding.message}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.actionRow}>
        <button type="button" onClick={onRecheck} disabled={rechecking} className={styles.secondaryButton}>
          {rechecking ? 'Re-checking…' : 'Re-check references'}
        </button>
        <button type="button" onClick={onDone} className={styles.secondaryButton}>
          Done
        </button>
      </div>
    </div>
  );
}

export function ImportFromGithubSection() {
  const navigate = useNavigate();
  const [browse, setBrowse] = useState<BrowseResult | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<Selection | null>(null);
  const [targetName, setTargetName] = useState('');
  const [report, setReport] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function loadAll() {
    const result = await configImportClient.browse();
    setBrowse(result);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  function toggleHistory(slug: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }

  function selectEntry(entry: ConfigListEntry) {
    setSelection({ source: 'github', entry });
    setTargetName(entry.jobTypeSlug);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelection({ source: 'upload', file });
    setTargetName(file.name.replace(/\.json$/i, ''));
  }

  function closeConfirm() {
    setSelection(null);
    setTargetName('');
    setReport(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleRowKeyDown(e: React.KeyboardEvent<HTMLDivElement>, entry: ConfigListEntry) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectEntry(entry);
    }
  }

  // Shared by both transports (design §4) — the confirm panel's Import
  // button funnels into whichever client call matches `selection.source`;
  // the resolution report that follows renders identically either way.
  async function handleImport() {
    if (!selection) return;
    setImporting(true);
    setImportError(null);
    try {
      const result =
        selection.source === 'github'
          ? await configImportClient.importFromGithub({ path: selection.entry.path, targetName })
          : await configImportClient.importUpload({ file: selection.file, targetName });
      setReport(result);
    } catch (err) {
      setImportError(messageForImportError(err));
    } finally {
      setImporting(false);
    }
  }

  async function handleRecheck() {
    if (!report) return;
    setRechecking(true);
    try {
      const result = await configImportClient.recheck(report.jobTypeId);
      setReport(result);
    } finally {
      setRechecking(false);
    }
  }

  // A finding's linkTarget navigates into the new job type at the relevant
  // tab and (for field-level findings) scrolls the named field into view —
  // delegated to the shared navigateToLinkTarget (lib/link-target-path) so
  // this mirrors JobVersionWorkspace.handleErrorClick's actual tab→surface
  // split instead of drifting from it (S-6 Task 8 controller ruling).
  function handleFindingClick(target: NonNullable<ImportFinding['linkTarget']>) {
    if (!report) return;
    navigateToLinkTarget(navigate, report.jobTypeId, target);
  }

  function handleOpenJobType() {
    if (!report) return;
    navigate(`/admin/job-types/${report.jobTypeId}`);
  }

  // The parent <section className={styles.section}><h2>Import from GitHub</h2>
  // already labels this surface (GithubBackupSection convention, #81).
  if (browse === null) {
    return <p>Loading…</p>;
  }

  const groups = browse.configured ? groupBySlug(browse.items) : [];

  return (
    <div>
      {!browse.configured ? (
        <p className={styles.emptyState}>
          Connect GitHub backup first to browse configs from your repository.
        </p>
      ) : (
        <div className={styles.importList} role="list">
          {browse.truncated && (
            <p className={styles.failuresLine} role="alert">
              The repository has more configs than could be listed — results may be incomplete.
            </p>
          )}
          {groups.length === 0 ? (
            <p className={styles.emptyState}>No configs found in the configured repository.</p>
          ) : (
            groups.map((group) => {
              const isExpanded = expanded.has(group.jobTypeSlug);
              return (
                <div key={group.jobTypeSlug} className={styles.importGroup} role="listitem">
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.importRow}
                    onClick={() => selectEntry(group.latest)}
                    onKeyDown={(e) => handleRowKeyDown(e, group.latest)}
                  >
                    <span className={styles.importRowSlug}>{group.jobTypeSlug}</span>
                    <span className={styles.importRowVersion}>v{group.latest.versionNumber}</span>
                    <span className={styles.importRowPath}>{group.latest.path}</span>
                    <span className={styles.importRowDate}>{formatPushedAt(group.latest.pushedAt)}</span>
                    {group.history.length > 0 && (
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHistory(group.jobTypeSlug);
                        }}
                      >
                        {isExpanded ? 'Hide history' : `History (${group.history.length})`}
                      </button>
                    )}
                  </div>
                  {isExpanded && (
                    <div className={styles.historyList}>
                      {group.history.map((entry) => (
                        <div
                          key={entry.path}
                          role="button"
                          tabIndex={0}
                          className={styles.historyRow}
                          onClick={() => selectEntry(entry)}
                          onKeyDown={(e) => handleRowKeyDown(e, entry)}
                        >
                          <span className={styles.importRowVersion}>v{entry.versionNumber}</span>
                          <span className={styles.importRowPath}>{entry.path}</span>
                          <span className={styles.importRowDate}>{formatPushedAt(entry.pushedAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <div className={styles.uploadRow}>
        <label htmlFor="config-import-upload" className={styles.fieldLabel}>
          Or upload a config file
        </label>
        <input
          id="config-import-upload"
          ref={fileInputRef}
          type="file"
          accept="application/json"
          onChange={handleFileChange}
          className={styles.textInput}
        />
      </div>

      {selection && (
        <div className={styles.confirmPanel}>
          <h3 className={styles.subHeading}>
            {selection.source === 'github'
              ? `Import ${selection.entry.jobTypeSlug} v${selection.entry.versionNumber}`
              : `Import ${selection.file.name}`}
          </h3>

          {report ? (
            <ImportReportView
              report={report}
              targetName={targetName}
              rechecking={rechecking}
              onRecheck={handleRecheck}
              onDone={closeConfirm}
              onFindingClick={handleFindingClick}
              onOpenJobType={handleOpenJobType}
            />
          ) : (
            <>
              <div className={styles.formGrid}>
                <label htmlFor="import-target-name" className={styles.fieldLabel}>
                  Target job type name
                </label>
                <input
                  id="import-target-name"
                  type="text"
                  value={targetName}
                  onChange={(e) => setTargetName(e.target.value)}
                  className={styles.textInput}
                />
              </div>
              {importError && (
                <p className={styles.testErr} role="alert">
                  {importError}
                </p>
              )}
              <div className={styles.actionRow}>
                <button type="button" onClick={closeConfirm} className={styles.secondaryButton}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing || targetName.trim().length === 0}
                  className={styles.primaryButton}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
